/**
 * Desk operations (owner OD-71; redesigned 2026-10-17 after the owner's review): a dashboard of what
 * waits (one tile per queue), the queue as rich cards, a review sheet per item — approve / reject /
 * complete / hold — and the user file (`OpsUser.tsx`). Commands are the panel's own server routes
 * (`/api/v1/desk/ops/*`); the device key silently signs the step-up. Amounts are the server's decimal
 * strings; nothing is computed here.
 */
import { cx, formatRelative } from '@hopex/ui';
import {
  ArrowRight,
  BadgeCheck,
  Banknote,
  Bitcoin,
  ChevronLeft,
  Coins,
  Landmark,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  UserRound,
} from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { api, errorMessage, ops } from '../lib/api.ts';
import { navigate, usePath, useVisible } from '../lib/platform.ts';
import { Empty, Loading, Pill } from './ops-kit.tsx';
import {
  QueueCard,
  ReviewSheet,
  type CryptoDeposit,
  type CryptoWithdrawal,
  type Item,
  type KycRow,
  type TomanDeposit,
  type TomanWithdrawal,
} from './ops-items.tsx';
import { UserOps } from './OpsUser.tsx';

// ───────── server shapes (subset the desk shows) ─────────
export interface OpsSummary {
  asOf: string;
  groups: string[];
  queues: { key: QueueKey; labelFa: string; count: number; oldest: string | null }[];
  can: { balance: boolean; accounts: boolean; kyc: boolean };
}
type QueueKey =
  | 'KYC1'
  | 'KYC2'
  | 'TOMAN_DEPOSITS'
  | 'TOMAN_WITHDRAWALS'
  | 'CRYPTO_DEPOSITS'
  | 'CRYPTO_WITHDRAWALS';

const QUEUE_ICON: Record<QueueKey, typeof BadgeCheck> = {
  KYC1: BadgeCheck,
  KYC2: ShieldCheck,
  TOMAN_DEPOSITS: Banknote,
  TOMAN_WITHDRAWALS: Landmark,
  CRYPTO_DEPOSITS: Coins,
  CRYPTO_WITHDRAWALS: Bitcoin,
};

export function useOpsSummary() {
  const [s, setS] = useState<OpsSummary | null>(null);
  const visible = useVisible();
  const load = useCallback(() => {
    api
      .get<OpsSummary>('/operations/summary')
      .then(setS)
      .catch(() => undefined);
  }, []);
  useEffect(() => {
    const t0 = window.setTimeout(load, 0);
    if (!visible) return () => window.clearTimeout(t0);
    const t = window.setInterval(load, 20_000);
    return () => {
      window.clearTimeout(t0);
      window.clearInterval(t);
    };
  }, [load, visible]);
  return { summary: s, reload: load };
}

export const opsEnabled = (s: OpsSummary | null) =>
  !!s && (s.queues.length > 0 || s.can.balance || s.can.accounts);

// ───────── screen ─────────
export function OpsScreen({ summary, reload }: { summary: OpsSummary | null; reload: () => void }) {
  const path = usePath();
  const queue = /^\/ops\/q\/([A-Z_0-9]+)$/.exec(path)?.[1] as QueueKey | undefined;
  const user = /^\/ops\/u\/([0-9a-f-]{36})(?:\/([a-z]+))?$/.exec(path);
  const can = summary?.can ?? { balance: false, accounts: false, kyc: false };
  return (
    <div className="flex min-h-0 flex-1 flex-col bg-canvas" data-ops>
      {user?.[1] ? (
        <UserOps key={user[1]} userId={user[1]} tab={user[2]} can={can} />
      ) : queue ? (
        <QueueView key={queue} queue={queue} summary={summary} onChanged={reload} />
      ) : (
        <OpsHome summary={summary} onRefresh={reload} />
      )}
    </div>
  );
}

function Bar({ title, back, children }: { title: string; back?: string; children?: ReactNode }) {
  return (
    <header className="flex shrink-0 items-center gap-2 border-b border-line bg-surface-1 px-3 py-2.5">
      {back && (
        <button
          type="button"
          onClick={() => navigate(back)}
          aria-label="بازگشت"
          className="hx-press grid size-10 cursor-pointer place-items-center rounded-full text-fg-muted hover:bg-surface-2"
        >
          <ArrowRight aria-hidden className="size-5" />
        </button>
      )}
      <h2 className="m-0 min-w-0 flex-1 truncate text-[15px] font-extrabold">{title}</h2>
      {children}
    </header>
  );
}

function OpsHome({ summary, onRefresh }: { summary: OpsSummary | null; onRefresh: () => void }) {
  const total = summary?.queues.reduce((s, q) => s + q.count, 0) ?? 0;
  const oldest = summary?.queues
    .map((q) => q.oldest)
    .filter((x): x is string => !!x)
    .sort()[0];
  return (
    <div className="min-h-0 flex-1 overflow-y-auto pb-[calc(var(--hx-sab,env(safe-area-inset-bottom))+20px)]">
      <div className="mx-auto flex max-w-5xl flex-col gap-3 p-3">
        <section
          className="relative overflow-hidden rounded-[26px] border border-line-strong bg-canvas-deep p-5 text-fg"
          data-theme-scope="dark"
        >
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(75%_140%_at_100%_0%,color-mix(in_srgb,var(--hx-accent)_28%,transparent),transparent_62%)]"
          />
          <div className="relative flex items-start justify-between gap-3">
            <div>
              <p className="m-0 text-[12.5px] text-fg-muted">کارهای در انتظار</p>
              <p className="m-0 mt-1 text-[34px] font-black leading-none" data-ops-total={total}>
                <span className="hx-num">{total.toLocaleString('fa-IR')}</span>{' '}
                <span className="text-[14px] font-bold text-fg-muted">مورد</span>
              </p>
              <p className="m-0 mt-2 text-[12px] text-fg-muted">
                {total === 0
                  ? 'همه‌چیز انجام شده است'
                  : oldest
                    ? `قدیمی‌ترین مورد: ${formatRelative(oldest)}`
                    : ' '}
              </p>
            </div>
            <button
              type="button"
              onClick={onRefresh}
              aria-label="تازه‌سازی"
              className="hx-press grid size-10 cursor-pointer place-items-center rounded-full bg-white/10 text-fg"
            >
              <RefreshCw aria-hidden className="size-4" />
            </button>
          </div>
        </section>
        {!summary ? (
          <Loading />
        ) : summary.queues.length === 0 ? (
          <Empty>برای شما صفی تعریف نشده است.</Empty>
        ) : (
          <div className="dk-stagger grid grid-cols-2 gap-2.5 lg:grid-cols-3">
            {summary.queues.map((q) => {
              const Icon = QUEUE_ICON[q.key];
              const busy = q.count > 0;
              return (
                <a
                  key={q.key}
                  href={`/ops/q/${q.key}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/ops/q/${q.key}`);
                  }}
                  data-ops-queue={q.key}
                  className={cx(
                    'hx-press hx-card relative flex min-h-[128px] flex-col justify-between gap-3 overflow-hidden rounded-[22px] border p-4 outline-hidden focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-focus',
                    busy
                      ? 'border-[color-mix(in_srgb,var(--hx-pending)_45%,transparent)] bg-[color-mix(in_srgb,var(--hx-pending)_7%,var(--hx-surface1))]'
                      : 'border-line bg-surface-1',
                  )}
                >
                  <span className="flex items-start justify-between gap-2">
                    <span
                      className={cx(
                        'grid size-10 place-items-center rounded-[13px]',
                        busy ? 'bg-accent text-on-accent shadow-cta' : 'bg-surface-2 text-fg-muted',
                      )}
                    >
                      <Icon aria-hidden className="size-5" />
                    </span>
                    <span
                      className={cx(
                        'hx-num text-[28px] font-black leading-none',
                        busy ? 'text-fg' : 'text-fg-subtle',
                      )}
                    >
                      {q.count.toLocaleString('fa-IR')}
                    </span>
                  </span>
                  <span>
                    <span className="block text-[13.5px] font-extrabold">{q.labelFa}</span>
                    <span className="mt-0.5 block text-[11px] text-fg-muted">
                      {busy && q.oldest ? `از ${formatRelative(q.oldest)}` : 'خالی'}
                    </span>
                  </span>
                </a>
              );
            })}
          </div>
        )}
        {summary && (summary.can.accounts || summary.can.balance || summary.can.kyc) && (
          <UserSearch />
        )}
      </div>
    </div>
  );
}

function UserSearch() {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<
    | { id: string; publicId: string; displayName: string | null; phoneMasked: string | null }[]
    | null
  >(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const find = async () => {
    if (q.trim().length < 3) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await ops.get<{ items: NonNullable<typeof rows> }>(
        `users?q=${encodeURIComponent(q.trim())}`,
      );
      setRows(r.items);
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="rounded-[22px] border border-line bg-surface-1 p-4" data-ops-search>
      <p className="m-0 mb-2.5 flex items-center gap-2 text-[14px] font-extrabold">
        <span className="grid size-8 place-items-center rounded-[11px] bg-accent-tint text-accent-text">
          <UserRound aria-hidden className="size-4" />
        </span>
        پرونده‌ی کاربر
      </p>
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void find();
        }}
      >
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="موبایل، کد ملی یا HX-…"
          dir="ltr"
          className="min-h-12 min-w-0 flex-1 rounded-[16px] border border-line-strong bg-surface-2 px-3.5 text-[16px] outline-hidden focus-visible:border-accent"
        />
        <button
          type="submit"
          disabled={busy}
          aria-label="جستجو"
          className="hx-press grid size-12 shrink-0 cursor-pointer place-items-center rounded-[16px] bg-accent text-on-accent shadow-cta disabled:opacity-60"
        >
          <Search aria-hidden className="size-5" />
        </button>
      </form>
      {err && <p className="m-0 mt-2 text-[12px] text-negative">{err}</p>}
      {rows && rows.length === 0 && (
        <p className="m-0 mt-2.5 text-[12.5px] text-fg-muted">کاربری با این مشخصات پیدا نشد.</p>
      )}
      {rows && rows.length > 0 && (
        <ul className="m-0 mt-2.5 grid list-none gap-2 p-0 sm:grid-cols-2">
          {rows.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                onClick={() => navigate(`/ops/u/${u.id}`)}
                className="hx-press flex w-full cursor-pointer items-center gap-3 rounded-[16px] border border-line bg-surface-2 px-3 py-2.5 text-start"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13.5px] font-bold">
                    {u.displayName ?? 'بدون نام'}
                  </span>
                  <bdi dir="ltr" className="block text-[11.5px] text-fg-muted">
                    {u.publicId} · {u.phoneMasked ?? '—'}
                  </bdi>
                </span>
                <ChevronLeft aria-hidden className="size-4 text-fg-subtle" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ───────── queues ─────────
async function loadQueue(key: QueueKey): Promise<Item[]> {
  const pick = <T,>(r: { items: T[] }) => r.items;
  switch (key) {
    case 'KYC1':
    case 'KYC2':
      return (
        pick(await ops.get<{ items: KycRow[] }>(`kyc/queue?level=${key === 'KYC1' ? '1' : '2'}`))
          // Only what waits for a person (the same rule as the server's queue count).
          .filter((r) =>
            r.level === 'KYC2'
              ? r.status === 'PENDING'
              : r.status === 'TEMPORARILY_UNAVAILABLE' ||
                (r.status === 'CHECKING' && Date.now() - Date.parse(r.waitingSince) > 120_000),
          )
          .map((v) => ({ k: 'KYC' as const, v }))
      );
    case 'TOMAN_DEPOSITS': {
      const a = pick(await ops.get<{ items: TomanDeposit[] }>('toman-deposits?status=SUBMITTED'));
      const b = pick(
        await ops.get<{ items: TomanDeposit[] }>('toman-deposits?status=UNDER_REVIEW'),
      );
      return [...a, ...b].map((v) => ({ k: 'TD' as const, v }));
    }
    case 'TOMAN_WITHDRAWALS': {
      const out: TomanWithdrawal[] = [];
      for (const s of ['PROCESSING', 'SECURITY_HOLD', 'SETTLEMENT_UNCERTAIN'])
        out.push(
          ...pick(await ops.get<{ items: TomanWithdrawal[] }>(`toman-withdrawals?status=${s}`)),
        );
      return out.map((v) => ({ k: 'TW' as const, v }));
    }
    case 'CRYPTO_DEPOSITS': {
      const a = pick(
        await ops.get<{ items: CryptoDeposit[] }>('crypto-deposits?status=UNDER_REVIEW'),
      );
      const b = pick(
        await ops.get<{ items: CryptoDeposit[] }>('crypto-deposits?status=TXID_SUBMITTED'),
      );
      return [...a, ...b].map((v) => ({ k: 'CD' as const, v }));
    }
    case 'CRYPTO_WITHDRAWALS': {
      const a = pick(
        await ops.get<{ items: CryptoWithdrawal[] }>('crypto-withdrawals?status=PROCESSING'),
      );
      const b = pick(
        await ops.get<{ items: CryptoWithdrawal[] }>('crypto-withdrawals?status=SECURITY_HOLD'),
      );
      return [...a, ...b].map((v) => ({ k: 'CW' as const, v }));
    }
  }
}

/** «همه»: the latest records of a money queue, any status (newest first; read-only when decided). */
async function loadHistory(key: QueueKey): Promise<Item[]> {
  const pick = <T,>(r: { items: T[] }) => r.items;
  switch (key) {
    case 'TOMAN_DEPOSITS':
      return pick(await ops.get<{ items: TomanDeposit[] }>('toman-deposits?page=1')).map((v) => ({
        k: 'TD' as const,
        v,
      }));
    case 'TOMAN_WITHDRAWALS':
      return pick(await ops.get<{ items: TomanWithdrawal[] }>('toman-withdrawals?page=1')).map(
        (v) => ({ k: 'TW' as const, v }),
      );
    case 'CRYPTO_DEPOSITS':
      return (
        pick(await ops.get<{ items: CryptoDeposit[] }>('crypto-deposits?page=1'))
          // An address the user never used is not a deposit yet.
          .filter((v) => v.status !== 'ADDRESS_ISSUED')
          .map((v) => ({ k: 'CD' as const, v }))
      );
    case 'CRYPTO_WITHDRAWALS':
      return pick(await ops.get<{ items: CryptoWithdrawal[] }>('crypto-withdrawals?page=1')).map(
        (v) => ({ k: 'CW' as const, v }),
      );
    default:
      return loadQueue(key);
  }
}

function QueueView({
  queue,
  summary,
  onChanged,
}: {
  queue: QueueKey;
  summary: OpsSummary | null;
  onChanged: () => void;
}) {
  const [items, setItems] = useState<Item[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<Item | null>(null);
  const money = !queue.startsWith('KYC');
  const [mode, setMode] = useState<'open' | 'all'>('open');
  const load = useCallback(() => {
    setItems(null);
    (mode === 'all' ? loadHistory(queue) : loadQueue(queue))
      .then((x) => {
        setItems(x);
        setErr(null);
      })
      .catch((e: unknown) => setErr(errorMessage(e)));
  }, [queue, mode]);
  useEffect(() => {
    const t = window.setTimeout(load, 0);
    return () => window.clearTimeout(t);
  }, [load]);
  const label = summary?.queues.find((q) => q.key === queue)?.labelFa ?? 'صف';
  return (
    <>
      <Bar title={label} back="/ops">
        {mode === 'open' && items && items.length > 0 && (
          <Pill tone="pending">{items.length.toLocaleString('fa-IR')}</Pill>
        )}
      </Bar>
      <div className="min-h-0 flex-1 overflow-y-auto pb-[calc(var(--hx-sab,env(safe-area-inset-bottom))+20px)]">
        <div className="mx-auto max-w-5xl p-3">
          {money && (
            <div
              role="tablist"
              aria-label="نمایش"
              className="mb-3 grid grid-cols-2 gap-1 rounded-full bg-surface-1 p-1"
            >
              {(
                [
                  ['open', 'در انتظار بررسی'],
                  ['all', 'همه‌ی موارد اخیر'],
                ] as const
              ).map(([m, l]) => (
                <button
                  key={m}
                  type="button"
                  role="tab"
                  aria-selected={mode === m}
                  onClick={() => setMode(m)}
                  data-ops-mode={m}
                  className={cx(
                    'min-h-10 cursor-pointer rounded-full text-[13px] font-bold',
                    mode === m ? 'bg-accent text-on-accent shadow-cta' : 'text-fg-muted',
                  )}
                >
                  {l}
                </button>
              ))}
            </div>
          )}
          {err && (
            <p role="alert" className="m-0 mt-6 text-center text-[13px] text-negative">
              {err}
            </p>
          )}
          {!items && !err && <Loading />}
          {items?.length === 0 && (
            <div className="mt-10 flex flex-col items-center gap-2 text-center text-fg-muted">
              <Sparkles aria-hidden className="size-8 text-accent-text" />
              <p className="m-0 text-[14px] font-bold">
                {mode === 'open' ? 'چیزی منتظر بررسی نیست' : 'موردی ثبت نشده است'}
              </p>
              {mode === 'open' && money && (
                <button
                  type="button"
                  onClick={() => setMode('all')}
                  className="cursor-pointer text-[12.5px] font-bold text-accent-text"
                >
                  دیدن همه‌ی موارد اخیر
                </button>
              )}
            </div>
          )}
          <ul className="dk-stagger m-0 grid list-none gap-2.5 p-0 lg:grid-cols-2">
            {items?.map((it) => (
              <li key={it.v.id}>
                <QueueCard
                  it={it}
                  onOpen={() =>
                    it.k === 'KYC' ? navigate(`/ops/u/${it.v.userId}/kyc`) : setOpen(it)
                  }
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
      {open && (
        <ReviewSheet
          item={open}
          onClose={() => setOpen(null)}
          onDone={() => {
            setOpen(null);
            load();
            onChanged();
          }}
        />
      )}
    </>
  );
}
